import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { productLabel } from "@/planner/lib/format";
import { priceForMargin, marginOnCost, money, ruleFromSettings, DEFAULT_RULE } from "@/planner/lib/pricing";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CATEGORIES } from "@/planner/lib/vendy";
import { toast } from "sonner";
import { Camera, ChevronDown, ChevronRight, ClipboardPaste, Paperclip, Pencil, Plus, ReceiptText, Sparkles, Trash2, X } from "lucide-react";

// Stock receipts (backend/planner/routes_receipts.py): enter a store receipt line by line, tie each
// line to the products it bought, and saving sets each product's landed unit cost, adds the units to
// backstock, and logs the receipt as an inventory expense. The photo goes to /api/expenses/receipts.

const SITE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORES = ["Sam's Club", "Costco", "BJ's", "Walmart", "Target"];
const STORE_KEY = "vortex_receipt_store";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));
// "KARS 40 CT", "UTRAZERO24PP", "OREO 30CT": the count in one pack.
const packFromText = (t) => {
  const m = /(\d+)\s*(?:CT|PK|PP|PACK|COUNT)\b/i.exec(t || "");
  return m ? Number(m[1]) : null;
};
let nextKey = 1;
const blankLine = (patch = {}) => ({ key: nextKey++, item_number: "", description: "", qty: "1", line_total: "", taxable: false, pack_units: "", items: [], source: null, ...patch });

// Phone photos are several MB; shrink to 2000px JPEG so the receipt stays readable but small.
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Could not read that file."));
  reader.onload = () => {
    if (!file.type.startsWith("image/") || file.type === "image/heic") { resolve(reader.result); return; }
    const img = new Image();
    img.onerror = () => resolve(reader.result);
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// Lines pasted from a spreadsheet or typed: item number, description, qty, total, T (taxed).
// Commas or tabs between fields; "2 @ 31.68" style quantities are fine as qty.
export const parsePasted = (text) => text.split(/\r?\n/).map((raw) => raw.trim()).filter(Boolean)
  .filter((row) => !/^item/i.test(row))
  .map((row) => {
    const f = row.split(/\t|,/).map((x) => x.trim());
    const [item_number = "", description = "", qty = "1", total = "", taxed = ""] = f;
    return blankLine({
      item_number, description, qty: String(num(qty) ?? 1), line_total: String(num(total.replace(/[$\s]/g, "")) ?? ""),
      taxable: /^(t|y|yes|true|1)$/i.test(taxed), pack_units: String(packFromText(description) ?? ""),
    });
  });

export const ProductPicker = ({ products, onPick, placeholder = "Add a product: type a name" }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hits = words.length
    ? products.filter((p) => { const l = `${productLabel(p)} ${p.size || ""} ${p.upc || ""}`.toLowerCase(); return words.every((w) => l.includes(w)); }).slice(0, 8)
    : [];
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={placeholder} className="h-8 text-xs" data-testid="receipt-product-search" />
      {open && hits.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg">
          {hits.map((p) => (
            <button key={p.id} type="button" className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(p); setQ(""); setOpen(false); }}>
              <span className="font-medium">{productLabel(p)}</span>{p.size && <span className="text-muted-foreground"> · {p.size}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// A product named from a receipt line, for items not in the catalog yet: "Kar's Sweet 'N Salty Mix
// 2 oz 40 ct" -> name "Kar's Sweet 'N Salty Mix", size "2 oz". The pack count becomes the case pack.
const SIZE_RE = /\b(\d+(?:\.\d+)?\s*(?:fl\.?\s*oz|oz|ml|l|liter|lb|g))\b/i;
const CATEGORY_WORDS = [["Energy", /monster|red ?bull|celsius|rockstar|bang|reign|energy/i], ["Water", /water|smartwater|fiji|deer park|dasani|aquafina/i],
  ["Sports", /gatorade|powerade|bodyarmor|propel/i], ["Tea", /\btea\b|pure leaf|arizona/i], ["Juice", /juice|lemonade/i],
  ["Soda", /coke|cola|pepsi|sprite|dew|dr\.? ?pepper|fanta|soda|7up|ginger ale/i],
  ["Chips", /chip|doritos|lay'?s|takis|cheetos|pretzel|cheez|popcorn|fritos|ruffles|pringles|sun ?chips/i],
  ["Candy", /candy|snickers|twix|m&m|mars|reese|skittles|sour patch|kit ?kat|hershey/i],
  ["Protein", /jerky|beef stick|protein|clif|quest|kind bar|nature valley|nut|trail mix/i]];
// "Gatorade Variety Pack 20 oz 24 pk": one line, several flavors (8 Cool Blue, 8 Fruit Punch, 8 Lemon Lime).
export const VARIETY_RE = /\bvariety\b|\bvar(?:iety)?\.?\s*p(?:ac)?k\b|\bass(?:or)?te?d\b|\bmix(?:ed)?\s*(?:pack|pk)\b/i;
export const productFromLine = (description) => {
  const d = (description || "").replace(/\(.*?\)/g, " ").replace(VARIETY_RE, " ");
  const size = (SIZE_RE.exec(d) || [])[1] || "";
  const name = d.replace(SIZE_RE, " ").replace(/\b\d+\s*(?:ct|pk|pp|pack|count)\b/gi, " ").replace(/\bbottles?\b|\bcans?\b/gi, " ")
    .replace(/\s+/g, " ").trim();
  const category = (CATEGORY_WORDS.find(([, re]) => re.test(d)) || ["Other"])[0];
  return { name, size: size.replace(/\s+/g, " "), category };
};

const NewProductForm = ({ line, sibling, onCreated, onCancel }) => {
  // Another flavor of a variety pack: start from the flavor already on the line, with the flavor blank.
  const [form, setForm] = useState(() => (sibling
    ? { name: sibling.name || "", size: sibling.size || "", category: sibling.category || productFromLine(line.description).category, variant: "",
        case_pack: sibling.case_pack || "", vend_price: sibling.vend_price != null ? String(sibling.vend_price) : "" }
    : { ...productFromLine(line.description), variant: "", case_pack: line.pack_units || "", vend_price: "" }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the product a name."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/products", { name: form.name.trim(), variant: form.variant.trim(), size: form.size.trim(), category: form.category,
        case_pack: num(form.case_pack) ? Math.round(num(form.case_pack)) : null, vend_price: num(form.vend_price) });
      toast.success(`${productLabel(data)} added to your products`);
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add the product."); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-dashed p-2" data-testid="receipt-new-product">
      <p className="mb-2 text-[11px] text-muted-foreground">New product from this line. Check the name; you can add a photo, barcode and price later on Products.</p>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-5"><Label className="text-[10px]">Name</Label><Input className="h-8 text-xs" value={form.name} onChange={set("name")} data-testid="new-product-name" /></div>
        <div className="col-span-6 sm:col-span-3"><Label className="text-[10px]">Flavor (optional)</Label><Input className="h-8 text-xs" value={form.variant} onChange={set("variant")} placeholder="e.g. Cherry" autoFocus={!!sibling} data-testid="new-product-flavor" /></div>
        <div className="col-span-6 sm:col-span-2"><Label className="text-[10px]">Size</Label><Input className="h-8 text-xs" value={form.size} onChange={set("size")} placeholder="1 oz" data-testid="new-product-size" /></div>
        <div className="col-span-6 sm:col-span-2"><Label className="text-[10px]">Category</Label>
          <select className="h-8 w-full rounded-md border bg-background px-2 text-xs" value={form.category} onChange={set("category")} data-testid="new-product-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div className="col-span-6 sm:col-span-2"><Label className="text-[10px]">Units per pack</Label><Input className="h-8 text-xs" type="number" min="1" value={form.case_pack} onChange={set("case_pack")} /></div>
        <div className="col-span-6 sm:col-span-2"><Label className="text-[10px]">Sell price ($)</Label><Input className="h-8 text-xs" type="number" min="0" step="0.05" inputMode="decimal" value={form.vend_price} onChange={set("vend_price")} placeholder="2.50" data-testid="new-product-price" /></div>
        <div className="col-span-6 flex items-end justify-end gap-2 sm:col-span-8">
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="button" size="sm" disabled={saving} onClick={save} data-testid="new-product-save">{saving ? "Adding…" : "Add product"}</Button>
        </div>
      </div>
    </div>
  );
};

// Spreads the line's units over its products (whole units; the first ones take the remainder).
const splitUnits = (total, n) => Array.from({ length: n }, (_, i) => Math.floor(total / n) + (i < total % n ? 1 : 0));

const LineEditor = ({ line, landed, products, byId, margin, onChange, onRemove, onProductCreated }) => {
  const [creating, setCreating] = useState(false);
  const variety = VARIETY_RE.test(line.description || "");
  const lineUnits = (num(line.qty) || 0) * (num(line.pack_units) || 0);
  const assigned = line.items.reduce((s, i) => s + (num(i.units) || 0), 0);
  const unitCost = assigned > 0 ? landed / assigned : null;
  const setItems = (items) => onChange({ items });
  const respread = (items, units = lineUnits) => (units > 0 ? splitUnits(units, items.length).map((u, i) => ({ ...items[i], units: String(u) })) : items);
  // A product made for this line replaces a best guess (the guess was wrong, or you'd have kept it).
  const addProduct = (p, replaceGuess = false) => {
    if (line.items.some((i) => i.product_id === p.id)) return;
    const kept = replaceGuess ? [] : line.items;
    const items = [...kept, { product_id: p.id, units: "", update_price: p.vend_price == null }];
    const packs = line.pack_units === "" && p.case_pack && kept.length === 0 ? String(p.case_pack) : line.pack_units;
    onChange({ ...line, pack_units: packs, source: replaceGuess ? null : line.source, items: respread(items, (num(line.qty) || 0) * (num(packs) || 0)) });
  };
  const set = (patch) => {
    const next = { ...line, ...patch };
    const units = (num(next.qty) || 0) * (num(next.pack_units) || 0);
    // Changing qty or pack size re-spreads the units over the products.
    if (("qty" in patch || "pack_units" in patch) && next.items.length) next.items = respread(next.items, units);
    onChange(next);
  };
  return (
    <Card className="p-3" data-testid="receipt-line">
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-4 sm:col-span-2"><Label className="text-[10px]">Item #</Label><Input className="h-8 text-xs" value={line.item_number} onChange={(e) => set({ item_number: e.target.value })} /></div>
        <div className="col-span-8 sm:col-span-4"><Label className="text-[10px]">On the receipt</Label><Input className="h-8 text-xs" value={line.description} onChange={(e) => set({ description: e.target.value, ...(line.pack_units === "" && packFromText(e.target.value) ? { pack_units: String(packFromText(e.target.value)) } : {}) })} /></div>
        <div className="col-span-3 sm:col-span-1"><Label className="text-[10px]">Qty</Label><Input className="h-8 text-xs" type="number" min="0" step="1" inputMode="numeric" value={line.qty} onChange={(e) => set({ qty: e.target.value })} /></div>
        <div className="col-span-4 sm:col-span-2"><Label className="text-[10px]">Units per pack</Label><Input className="h-8 text-xs" type="number" min="1" inputMode="numeric" value={line.pack_units} placeholder="24" onChange={(e) => set({ pack_units: e.target.value })} data-testid="receipt-pack-units" /></div>
        <div className="col-span-5 sm:col-span-2"><Label className="text-[10px]">Paid ($)</Label><Input className="h-8 text-xs" type="number" min="0" step="0.01" inputMode="decimal" value={line.line_total} onChange={(e) => set({ line_total: e.target.value })} /></div>
        <div className="col-span-12 flex items-end justify-between gap-2 sm:col-span-1 sm:flex-col sm:items-end">
          <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={line.taxable} onChange={(e) => set({ taxable: e.target.checked })} />Taxed</label>
          <button type="button" className="rounded p-1 text-muted-foreground hover:text-destructive" onClick={onRemove} aria-label="Remove line"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-2 space-y-1.5">
        {line.items.map((it, idx) => {
          const p = byId[it.product_id];
          const suggested = unitCost ? priceForMargin(unitCost, p?.target_margin_pct ?? margin) : null;
          const typed = num(it.vend_price);
          const price = typed ?? p?.vend_price ?? null;
          const earned = unitCost && price != null ? marginOnCost(unitCost, price) : null;
          const setItem = (patch) => setItems(line.items.map((x, j) => (j === idx ? { ...x, ...patch } : x)));
          return (
            <div key={it.product_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/50 px-2 py-1.5 text-xs" data-testid="receipt-line-item">
              <span className="min-w-0 flex-1 font-medium">{p ? productLabel(p) : "Unknown product"}{p?.size && <span className="font-normal text-muted-foreground"> · {p.size}</span>}</span>
              {line.items.length > 1 && (
                <label className="flex items-center gap-1">units <Input className="h-7 w-16 text-xs" type="number" min="1" value={it.units}
                  onChange={(e) => setItems(line.items.map((x, j) => (j === idx ? { ...x, units: e.target.value } : x)))} data-testid="receipt-item-units" />
                  {num(line.qty) > 1 && num(it.units) > 0 && <span className="text-muted-foreground">({+(num(it.units) / num(line.qty)).toFixed(1)} per pack)</span>}
                </label>
              )}
              {unitCost != null && <span>cost <b>{money(unitCost)}</b>{p?.cost != null && Math.abs(p.cost - unitCost) >= 0.005 && <span className="text-muted-foreground"> (was {money(p.cost)})</span>}</span>}
              <label className="flex items-center gap-1" title="What you'll charge. Leave blank to keep the current price.">sell $
                <Input className="h-7 w-20 text-xs" type="number" min="0" step="0.05" inputMode="decimal" value={it.vend_price ?? ""}
                  placeholder={p?.vend_price != null ? Number(p.vend_price).toFixed(2) : "price"} aria-label={`Sell price for ${p ? productLabel(p) : "item"}`}
                  onChange={(e) => setItem({ vend_price: e.target.value })} data-testid="receipt-sell-price" />
              </label>
              {earned != null && <span className={earned < (p?.target_margin_pct ?? margin) ? "text-destructive" : "text-green-700"}>{earned}% markup</span>}
              {typed == null && suggested > 0 && suggested !== p?.vend_price && (
                <label className="flex items-center gap-1" title={`Cost + ${p?.target_margin_pct ?? margin}%, rounded up to the next quarter`}>
                  <input type="checkbox" checked={!!it.update_price} onChange={(e) => setItem({ update_price: e.target.checked })} data-testid="receipt-update-price" />
                  or use {money(suggested)}
                </label>
              )}
              <button type="button" className="text-muted-foreground hover:text-destructive" aria-label="Remove product" onClick={() => setItems(respread(line.items.filter((_, j) => j !== idx)))}><X className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-80">
            <ProductPicker products={products} onPick={(p) => addProduct(p)} placeholder={line.items.length ? "Add another flavor: type a name" : undefined} />
          </div>
          {!creating && <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setCreating(true)} data-testid="receipt-create-product"><Plus className="mr-1 h-3.5 w-3.5" />{variety && line.items.length ? "New flavor" : "New product"}</Button>}
          {line.source && line.items.length > 0 && <Badge variant="outline" className="text-[10px]">{line.source === "guess" ? "best guess, check it" : `matched by ${line.source}`}</Badge>}
          {variety && line.items.length < 2 && <span className="w-full text-[11px] text-amber-700" data-testid="receipt-variety-hint">Variety pack: add each flavor in it. The units split evenly; change them if the mix isn't even.</span>}
          {line.items.length === 0 && <span className="text-[11px] text-muted-foreground">No product: counted in the total only (supplies, not for resale).</span>}
          {line.items.length > 0 && !assigned && <span className="text-[11px] text-amber-700">Enter units per pack to get a unit cost.</span>}
          {line.items.length > 1 && lineUnits > 0 && assigned !== lineUnits && <span className="text-[11px] text-amber-700">{assigned} of {lineUnits} units assigned</span>}
        </div>
        {creating && <NewProductForm line={line} sibling={variety ? byId[line.items[0]?.product_id] : null} onCancel={() => setCreating(false)}
          onCreated={(p) => { setCreating(false); onProductCreated(p); addProduct(p, line.source === "guess" && !variety); }} />}
      </div>
    </Card>
  );
};

// A saved receipt back into the form's lines. Typed prices start blank: the product keeps its current price.
const linesFromReceipt = (r) => r.lines.map((l) => blankLine({
  item_number: l.item_number || "", description: l.description || "", qty: String(l.qty ?? 1), line_total: String(l.line_total ?? ""),
  taxable: !!l.taxable, pack_units: l.pack_units ? String(l.pack_units) : "",
  items: (l.items || []).map((i) => ({ product_id: i.product_id, units: String(i.units), update_price: false, vend_price: "" })),
}));

// New receipt, or `editing` a saved one (PUT moves backstock by the difference and updates the expense).
const NewReceipt = ({ products, margin, readerOn, onSaved, onCancel, onProductCreated, editing = null }) => {
  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const [store, setStore] = useState(() => { if (editing) return editing.store; try { return localStorage.getItem(STORE_KEY) || "Sam's Club"; } catch { return "Sam's Club"; } });
  const [date, setDate] = useState(editing?.date || today());
  const [tax, setTax] = useState(editing?.tax ? String(editing.tax) : "");
  const [printedTotal, setPrintedTotal] = useState(null);
  const [file, setFile] = useState(editing?.file_id ? { id: editing.file_id, preview: null } : null);       // {id, preview}
  const [lines, setLines] = useState(() => (editing ? linesFromReceipt(editing) : []));
  const [paste, setPaste] = useState(null);
  const [busy, setBusy] = useState("");
  const [logExpense, setLogExpense] = useState(editing ? !!editing.expense_id : true);
  const [exempt, setExempt] = useState(!!editing?.tax_exempt);
  const fileInput = useRef(null);

  const subtotal = lines.reduce((s, l) => s + (num(l.line_total) || 0), 0);
  const taxAmt = exempt ? 0 : num(tax) || 0;
  const total = subtotal + taxAmt;
  const landed = useMemo(() => {
    const t = exempt ? 0 : num(tax) || 0;
    const base = lines.map((l) => (l.taxable ? num(l.line_total) || 0 : 0));
    const b = base.some((x) => x > 0) ? base : lines.map((l) => num(l.line_total) || 0);
    const sum = b.reduce((s, x) => s + x, 0);
    return lines.map((l, i) => (num(l.line_total) || 0) + (sum ? (t * b[i]) / sum : 0));
  }, [lines, tax, exempt]);

  const match = async (newLines, storeName = store) => {
    if (!newLines.length) return newLines;
    try {
      const { data } = await api.post("/receipts/match", { store: storeName, lines: newLines.map((l) => ({ item_number: l.item_number, description: l.description, catalog_match: l.catalog_match || null })) });
      return newLines.map((l, i) => {
        const m = data.lines[i];
        if (!m?.items?.length || l.items.length) return l;
        const pack = l.pack_units || (m.pack_units ? String(m.pack_units) : "");
        const qty = num(l.qty) || 1;
        const items = m.items.map((it) => ({ product_id: it.product_id, units: "", share: it.share, update_price: byId[it.product_id]?.vend_price == null }));
        const units = qty * (num(pack) || 0);
        // Remembered variety cases come back as units per pack bought; scale by qty.
        const spread = m.items.length > 1 && m.items.every((it) => it.share)
          ? items.map((it) => ({ ...it, units: String(Math.round(it.share * qty)) }))
          : units > 0 ? splitUnits(units, items.length).map((u, j) => ({ ...items[j], units: String(u) })) : items;
        return { ...l, pack_units: pack, items: spread, source: m.source };
      });
    } catch { return newLines; }
  };

  const attach = async (f) => {
    if (!f) return;
    setBusy("upload");
    try {
      const data = await fileToDataUrl(f);
      const res = await api.post(`${SITE_API}/expenses/receipts`, { data, filename: f.name });
      setFile({ id: res.data.id, preview: data.startsWith("data:image") ? data : null });
      toast.success("Receipt photo attached.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload the receipt.");
    } finally { setBusy(""); }
  };

  const readIt = async () => {
    setBusy("read");
    try {
      const { data } = await api.post("/receipts/read", { file_id: file.id });
      if (!data.is_receipt || !data.lines.length) { toast.error("Couldn't find item lines on that photo. Paste or type them instead."); return; }
      const storeName = data.store_name || store;
      if (data.store_name) setStore(data.store_name);
      if (data.date) setDate(data.date);
      if (data.tax != null) setTax(String(data.tax));
      setPrintedTotal(data.total);
      const read = data.lines.map((l) => {
        const pack = l.pack_units ? String(l.pack_units) : "";
        const qty = l.qty || 1;
        const units = qty * (l.pack_units || 0);
        const items = (l.items || []).map((it) => ({ product_id: it.product_id, update_price: byId[it.product_id]?.vend_price == null, units: "" }));
        const spread = l.items?.length > 1 && l.items.every((it) => it.share)
          ? l.items.map((it, j) => ({ ...items[j], units: String(Math.round(it.share * qty)) }))
          : units > 0 && items.length ? splitUnits(units, items.length).map((u, j) => ({ ...items[j], units: String(u) })) : items;
        return blankLine({ item_number: l.item_number, description: l.description, qty: String(qty), line_total: String(l.line_total),
          taxable: l.taxable, pack_units: pack, items: spread, source: l.source || null, catalog_match: l.catalog_match });
      });
      setLines(await match(read, storeName));
      toast.success(`Read ${read.length} lines. Check the products and units per pack.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read the receipt.");
    } finally { setBusy(""); }
  };

  const addPasted = async () => {
    const parsed = parsePasted(paste || "");
    if (!parsed.length) { toast.error("Nothing to add. One line per item: item #, description, qty, total, T"); return; }
    setLines([...lines, ...(await match(parsed))]);
    setPaste(null);
  };

  const save = async () => {
    const bad = lines.find((l) => num(l.line_total) == null);
    if (bad) { toast.error(`Enter what you paid for ${bad.description || "every line"}.`); return; }
    const noUnits = lines.find((l) => l.items.length && l.items.some((i) => !(num(i.units) > 0)));
    if (noUnits) { toast.error(`Enter units per pack for ${noUnits.description || "each line with a product"}.`); return; }
    setBusy("save");
    try {
      try { localStorage.setItem(STORE_KEY, store); } catch { /* storage blocked */ }
      const { data } = await api[editing ? "put" : "post"](editing ? `/receipts/${editing.id}` : "/receipts", {
        store, date, tax: taxAmt, tax_exempt: exempt, file_id: file?.id || "", log_expense: logExpense,
        lines: lines.map((l) => ({ item_number: l.item_number, description: l.description, qty: num(l.qty) || 1,
          line_total: num(l.line_total), taxable: l.taxable, pack_units: num(l.pack_units),
          items: l.items.map((i) => ({ product_id: i.product_id, units: num(i.units), update_price: !!i.update_price, vend_price: num(i.vend_price) })) })),
      });
      toast.success(editing ? "Receipt updated. Backstock, costs and the expense follow the changes." : `Saved. ${data.units} units added to backstock.`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the receipt.");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="new-receipt">
      {editing && <p className="text-sm text-muted-foreground" data-testid="receipt-editing">Editing the {editing.store} receipt from {new Date(`${editing.date}T12:00:00`).toLocaleDateString()}. Saving moves backstock by the difference, so units already in machines stay counted.</p>}
      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <div><Label>Store</Label><Input value={store} onChange={(e) => setStore(e.target.value)} list="receipt-stores" data-testid="receipt-store" /><datalist id="receipt-stores">{STORES.map((s) => <option key={s} value={s} />)}</datalist></div>
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="sm:col-span-2">
            <Label>Receipt photo</Label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={busy === "upload"} onClick={() => { fileInput.current.setAttribute("capture", "environment"); fileInput.current.click(); }}><Camera className="mr-1.5 h-4 w-4" />{file ? "Retake" : "Take photo"}</Button>
              <Button type="button" variant="outline" size="sm" disabled={busy === "upload"} onClick={() => { fileInput.current.removeAttribute("capture"); fileInput.current.click(); }}><Paperclip className="mr-1.5 h-4 w-4" />Choose file</Button>
              <input ref={fileInput} type="file" accept="image/*,application/pdf" className="sr-only" data-testid="receipt-photo" onChange={(e) => { attach(e.target.files[0]); e.target.value = ""; }} />
              {busy === "upload" && <span className="text-xs text-muted-foreground">Uploading…</span>}
              {file && <span className="text-xs text-green-700">Attached</span>}
              {file && readerOn && <Button type="button" size="sm" onClick={readIt} disabled={busy === "read"} data-testid="receipt-read"><Sparkles className="mr-1.5 h-4 w-4" />{busy === "read" ? "Reading…" : "Read the lines for me"}</Button>}
            </div>
          </div>
        </div>
        {file?.preview && <img src={file.preview} alt="Receipt" className="mt-3 max-h-48 rounded border object-contain" />}
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto font-heading text-lg font-semibold">Lines <span className="text-sm font-normal text-muted-foreground">({lines.length})</span></h2>
        <Button type="button" variant="outline" size="sm" onClick={() => setPaste(paste == null ? "" : null)} data-testid="receipt-paste-toggle"><ClipboardPaste className="mr-1.5 h-4 w-4" />Paste lines</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, blankLine()])} data-testid="receipt-add-line"><Plus className="mr-1.5 h-4 w-4" />Add line</Button>
      </div>
      {paste != null && (
        <Card className="space-y-2 p-3">
          <p className="text-xs text-muted-foreground">One item per line: <span className="font-mono">item #, description, qty, total paid, T</span> (T if taxed). Tabs work too, so you can paste from a spreadsheet.</p>
          <textarea className="h-32 w-full rounded-md border bg-background p-2 font-mono text-xs text-foreground placeholder:text-muted-foreground" value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={"631556, UTRAZERO24PP, 2, 84.96, T\n980101300, KARS 40 CT, 1, 19.36"} data-testid="receipt-paste" />
          <Button type="button" size="sm" onClick={addPasted} data-testid="receipt-paste-add">Add these lines</Button>
        </Card>
      )}
      {lines.length === 0 && paste == null && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {readerOn ? "Attach the receipt photo and tap \"Read the lines for me\", or paste or add lines yourself." : "Paste the lines or add them one by one. Each line you tie to a product sets its cost and adds to backstock."}
        </Card>
      )}
      <div className="space-y-2">
        {lines.map((l, i) => (
          <LineEditor key={l.key} line={l} landed={landed[i]} products={products} byId={byId} margin={margin}
            onChange={(patch) => setLines(lines.map((x) => (x.key === l.key ? { ...x, ...patch } : x)))}
            onRemove={() => setLines(lines.filter((x) => x.key !== l.key))} onProductCreated={onProductCreated} />
        ))}
      </div>

      {lines.length > 0 && (
        <Card className="sticky bottom-2 z-10 flex flex-wrap items-end gap-4 p-4 shadow-lg">
          <div className="text-sm">Subtotal <b>{money(subtotal)}</b></div>
          <div className="w-28"><Label className="text-xs">Sales tax ($)</Label><Input className="h-8" type="number" step="0.01" min="0" value={exempt ? "" : tax} placeholder={exempt ? "exempt" : "0.00"} disabled={exempt} onChange={(e) => setTax(e.target.value)} data-testid="receipt-tax" /></div>
          <label className="flex items-center gap-1.5 text-xs" title="Bought for resale with your exemption certificate, so no sales tax was charged"><input type="checkbox" checked={exempt} onChange={(e) => setExempt(e.target.checked)} data-testid="receipt-exempt" />Tax exempt</label>
          <div className="text-sm">Total <b data-testid="receipt-total">{money(total)}</b>
            {printedTotal != null && Math.abs(printedTotal - total) > 0.01 && <span className="ml-2 text-xs text-amber-700">receipt says {money(printedTotal)}</span>}
          </div>
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={logExpense} onChange={(e) => setLogExpense(e.target.checked)} />Log as an inventory expense for taxes</label>
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy === "save"} data-testid="receipt-save">{busy === "save" ? "Saving…" : editing ? "Save changes" : "Save receipt"}</Button>
          </div>
        </Card>
      )}
    </div>
  );
};

const ReceiptRow = ({ r, canEdit, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const viewPhoto = async () => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`${SITE_API}/expenses/receipts/${r.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.location.href = url;
    } catch { win?.close(); toast.error("Couldn't open the photo."); }
  };
  return (
    <Card className="p-3" data-testid={`receipt-${r.id}`}>
      <div className="flex items-center gap-3">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setOpen(!open)}>
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className="font-semibold">{r.store}</span>
          <span className="text-sm text-muted-foreground">{new Date(`${r.date}T12:00:00`).toLocaleDateString()}</span>
          <span className="hidden text-xs text-muted-foreground sm:inline">{r.lines.length} lines · {r.units} units</span>
          {r.tax_exempt ? <Badge variant="outline" className="text-[10px]">tax exempt</Badge> : r.tax > 0 && <span className="text-xs text-muted-foreground">{money(r.tax)} tax</span>}
        </button>
        <span className="font-mono font-semibold">{money(r.total)}</span>
        {r.file_id && <Button variant="ghost" size="sm" onClick={viewPhoto}>Photo</Button>}
        {canEdit && <Button variant="ghost" size="icon" aria-label="Edit receipt" title="Edit" onClick={() => onEdit(r)} data-testid="receipt-edit"><Pencil className="h-4 w-4" /></Button>}
        {canEdit && <Button variant="ghost" size="icon" aria-label="Delete receipt" onClick={() => onDelete(r)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Line</th><th className="pr-2">Products</th><th className="pr-2 text-right">Units</th><th className="pr-2 text-right">Paid</th><th className="text-right">Unit cost</th></tr></thead>
            <tbody>
              {r.lines.map((l, i) => (
                <tr key={i} className="border-t align-top">
                  <td className="py-1 pr-2"><span className="font-mono text-muted-foreground">{l.item_number}</span> {l.description}</td>
                  <td className="pr-2">{l.items.length ? l.items.map((it) => <div key={it.product_id}>{it.name}{it.price_updated && <span className="text-green-700"> → {money(it.vend_price)}</span>}</div>) : <span className="text-muted-foreground">not for resale</span>}</td>
                  <td className="pr-2 text-right">{l.units || ""}</td>
                  <td className="pr-2 text-right">{money(l.landed_cost)}</td>
                  <td className="text-right">{l.unit_cost != null ? money(l.unit_cost) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-[11px] text-muted-foreground">Paid includes this line's share of the {money(r.tax)} sales tax.</p>
        </div>
      )}
    </Card>
  );
};

export default function Receipts() {
  const { canEdit } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [products, setProducts] = useState([]);
  const [margin, setMargin] = useState(DEFAULT_RULE);
  const [readerOn, setReaderOn] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/receipts"), api.get("/products")]);
    setReceipts(r.data); setProducts(p.data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Couldn't load receipts."));
    api.get("/settings").then((r) => setMargin(ruleFromSettings(r.data))).catch(() => {});
    api.get("/receipts/reader").then((r) => setReaderOn(!!r.data.configured)).catch(() => {});
  }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.store} receipt from ${r.date}? Its ${r.units} units come back out of backstock and its expense is removed. Costs and prices it set stay.`)) return;
    try { await api.delete(`/receipts/${r.id}`); toast.success("Receipt deleted."); load(); }
    catch { toast.error("Couldn't delete it."); }
  };

  const thisYear = receipts.filter((r) => r.date.slice(0, 4) === String(new Date().getFullYear()));
  const spent = thisYear.reduce((s, r) => s + r.total, 0);
  const taxPaid = thisYear.reduce((s, r) => s + (r.tax || 0), 0);
  return (
    <div data-testid="receipts-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Stock Receipts</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">Enter what you bought. Each product gets its real cost per unit (tax included) for pricing, the units go into backstock, and the receipt is logged for taxes.</p>
        </div>
        {!adding && !editing && canEdit && <Button onClick={() => setAdding(true)} data-testid="receipt-new"><Plus className="mr-1.5 h-4 w-4" />New receipt</Button>}
      </div>
      {adding || editing ? (
        <NewReceipt key={editing?.id || "new"} editing={editing} products={products} margin={margin} readerOn={readerOn}
          onCancel={() => { setAdding(false); setEditing(null); }}
          onProductCreated={(p) => setProducts((ps) => [...ps.filter((x) => x.id !== p.id), p])}
          onSaved={() => { setAdding(false); setEditing(null); load(); }} />
      ) : receipts.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><ReceiptText className="mx-auto mb-2 h-8 w-8" />No receipts yet. Add your last stock run to set costs and backstock.</Card>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground" data-testid="receipts-year">{money(spent)} on stock this year across {thisYear.length} receipt{thisYear.length === 1 ? "" : "s"}, including {money(taxPaid)} in sales tax.</p>
          {receipts.map((r) => <ReceiptRow key={r.id} r={r} canEdit={canEdit} onEdit={(x) => { setEditing(x); window.scrollTo(0, 0); }} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
